import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ErrorHandler,
  Inject,
  inject,
  LOCALE_ID,
  ModuleWithProviders,
  NgModule,
  Type,
  DOCUMENT,
  provideAppInitializer,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { RouterState, StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { LangChangeEvent, TranslateLoader, TranslateModule, TranslateParser, TranslateService } from '@ngx-translate/core';
import { tap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { metaReducers, reducers } from '@iris/common/redux/reducers';
import { IrisLoaderService } from './common/services/loader.service';
import { IrisAuthInterceptor } from './common/utils/handlers/iris-auth.interceptor';
import { IrisBasePathInterceptor } from './common/utils/handlers/iris-base-path.interceptor';
import { IrisErrorHandlerInterceptor } from './common/utils/handlers/iris-error-handler.interceptor';
import { IrisEncodeUrlParamsSafelyInterceptor } from './common/utils/handlers/iris-encode-url-params-safely.interceptor';
import { IrisTranslationLoader } from './common/utils/iris-translation.loader';
import { IrisInitEffects } from './common/modules/init-store/effects/init.effects';
import { IrisResourceRolesEffects } from '@iris/common/redux/effects/resource-roles.effects';
import { ToastService, SK_TOAST_DURATION } from '@ngx-strakit/cdk/controls/toast';
import { IrisUsersEffects } from './common/redux/effects/users.effects';
import { UnitsEffects } from './common/redux/effects/units.effects';
import { IrisLoaderEffects } from './common/redux/effects/loaders.effects';
import { IrisTimePipe, IrisUserTimePipe } from '@iris/common/modules/time';
import { HotkeyModule, HotkeysService } from 'angular2-hotkeys';
import { IrisSettingsEffects } from '@iris/common/redux/effects/settings.effects';
import { ProjectsEffects } from '@iris/common/redux/effects/projects.effects';
import { ApiQueryModule, IrisApiQueryInitializerService } from '@iris/api-query';
import { AppInitializer } from './app.init';
import { IrisGoogleMapsIntegrationModule } from '@iris/common/modules/google-maps-integration/for-root.module';
import { IrisProjectsMembersChangedNotificationModule } from '@iris/modules/projects/details/project-changes-notification/members';
import { IrisCompaniesService } from '@iris/common/services/companies.service';
import { AppRootComponent } from 'app/app-root.component';
import { AppRootComponentModule } from 'app/app-root.component.module';
import { AuthModule } from 'app/iris-modules/auth/auth.module';
import { CookieService } from 'ngx-cookie-service';
import { TIMEZONE } from '@iris/common/tokens';
import { of, take } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { TitleStrategy } from '@angular/router';
import { IrisSentryIntegrationModule } from './common/modules/sentry/sentry.module';
import { LocaleService } from '@iris/common/services/locale.service';
import { RumIntegrationModule } from '@iris/common/modules/rum/rum-integration.module';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialog } from '@angular/material/dialog';
import { ValidationMessageResolverModule } from '@iris/common/modules/fields/validation/validation-message-resolver/validation-message-resolver.module';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { PageTitleStrategyService } from '@iris/common/services/page-name/page-title-strategy.service';
import { BackendStompService, backendStompServiceFactory } from '@iris/common/services/websockets/backend.stomp-service';
import { NotificationsStompService, notificationsStompServiceFactory } from '@iris/common/services/websockets/notifications.stomp-service';
import { IrisMsalModule } from '@iris/modules/msal/msal.module';
import { IrisHotkeysService } from './common/services/utils/iris-hotkeys.service';
import { MAT_LIST_CONFIG } from '@angular/material/list';
import { Settings } from 'luxon';
import { IrisUserService } from '@iris/common/services/user.service';
import { IrisEnvironmentService } from '@iris/common/services/environment.service';
import { IrisTranslationParser } from '@iris/common/utils/iris-translation.parser';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/button-toggle';
import { UnleashService } from '@karelics/angular-unleash-proxy-client';
import { IrisAzureAuthInterceptor } from './common/utils/handlers/iris-azure-auth.interceptor';
import { ElasticTransactionInterceptor } from '@iris/common/modules/rum/elastic-transaction.interceptor';
import { IrisSetServiceNameInterceptor } from '@iris/common/utils/handlers/iris-set-service-name.interceptor';
import { SK_EDITOR_DEFAULT_LANGUAGE } from '@ngx-strakit/cdk/controls/editor';
import { AppComponent } from './app.component';
import { provideTranslator, STRAKIT_STYLING_CONTROL_CONFIG, StrakitStylingControlConfigI, StylingMode } from '@ngx-strakit/cdk';
import { provideHighcharts } from 'highcharts-angular';

import { IrisCommonSiaModule } from './common/modules/sia/sia.module';
import { STRAKIT_TRANSLATIONS_DICT } from '@iris/common/utils/strakitTranslateDict.utils';
import { provideSkDatetime } from '@ngx-strakit/cdk/controls';
import { datetimeFormatsFactory } from './common/modules/time';
import { IrisThemeEffects } from '@iris/common/redux/effects/theme.effects';
import { IrisApplicationsEffects } from '@iris/common/redux/effects/applications.effects';
import { IrisApplicationsService } from '@iris/common/services/utils/iris-applications.service';
import { provideSkSelectConfig } from '@iris/common/modules/fields/sk-select-field/select-config.sk.provider';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import {
  translationsProxyRewriteInterceptor,
} from '@iris/common/utils/handlers/translations-proxy-rewrite.interceptor';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const developmentModeImports: (any[] | Type<any> | ModuleWithProviders<Record<string, any>>)[] = [
  StoreDevtoolsModule.instrument({
    name: 'IRIS Store DevTools',
    logOnly: environment.production,
  }),
];
@NgModule({
  imports: [
    IrisSentryIntegrationModule,
    RumIntegrationModule,
    BrowserModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: IrisTranslationLoader,
        deps: [HttpClient],
      },
      parser: {
        provide: TranslateParser,
        useClass: IrisTranslationParser,
      },
    }),
    BrowserAnimationsModule,
    IrisMsalModule,
    AppRootComponentModule,
    AppComponent,
    AppRoutingModule,
    IrisGoogleMapsIntegrationModule.forRoot({
      deps: [IrisEnvironmentService],
      useFactory: (envService: IrisEnvironmentService) => envService.env.gmapsApiKey,
    }),
    HotkeyModule.forRoot(),
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
      },
    }),
    EffectsModule.forRoot([
      IrisInitEffects,
      IrisUsersEffects,
      IrisLoaderEffects,
      UnitsEffects,
      IrisApplicationsEffects,
      IrisSettingsEffects,
      IrisResourceRolesEffects,
      ProjectsEffects,
      IrisThemeEffects,
    ]),
    StoreRouterConnectingModule.forRoot({ routerState: RouterState.Minimal }),

    ...(environment.production ? [] : developmentModeImports),

    ApiQueryModule.forRoot({
      withMicroserviceRedirect: !environment.production,
    }),
    IrisProjectsMembersChangedNotificationModule,
    AuthModule,
    ValidationMessageResolverModule.forModule(),
    IrisCommonSiaModule.forRoot(),
  ],
  providers: [
    IrisCompaniesService,
    {
      provide: BackendStompService,
      useFactory: backendStompServiceFactory,
    },
    {
      provide: NotificationsStompService,
      useFactory: notificationsStompServiceFactory,
    },
    {
      provide: LocationStrategy,
      useClass: PathLocationStrategy,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: IrisBasePathInterceptor,
      multi: true,
    },
    // The order of HTTP interceptors is important:
    // - `IrisAzureAuthInterceptor` (first) ensures Microsoft authentication headers are set before any other authentication logic.
    // - `IrisAuthInterceptor` (last) handles general authentication and should remain at the end to prevent conflicts and ensure
    //   any necessary token modifications are applied as the final authentication step.
    {
      provide: HTTP_INTERCEPTORS,
      useClass: IrisAzureAuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: IrisAuthInterceptor,
      multi: true,
    },
    // Possibly should be removed or changed after and if
    // will be merged some of this PRs
    // https://github.com/angular/angular/pull/32598
    // https://github.com/angular/angular/pull/37385
    {
      provide: HTTP_INTERCEPTORS,
      useClass: IrisEncodeUrlParamsSafelyInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: IrisErrorHandlerInterceptor,
      multi: true,
      deps: [
        TranslateService,
        IrisLoaderService,
        ToastService,
      ],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ElasticTransactionInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: IrisSetServiceNameInterceptor,
      multi: true,
    },
    IrisTimePipe,
    IrisUserTimePipe,
    IrisApiQueryInitializerService,
    provideAppInitializer(AppInitializer),
    provideAppInitializer(() => {
      const unleashService = inject(UnleashService);
      return unleashService.initialized$.pipe(take(1), tap(() => void unleashService.unleash.start()));
    }),
    CookieService,
    {
      provide: TIMEZONE,
      useFactory: (userService: IrisUserService) => of(userService.me.timeZone),
      deps: [IrisUserService],
    },
    {
      provide: SK_EDITOR_DEFAULT_LANGUAGE,
      useFactory: (userService: IrisUserService) => userService.me.language || 'en',
      deps: [IrisUserService],
    },
    {
      provide: STRAKIT_STYLING_CONTROL_CONFIG,
      useFactory: () => {
        const globalSandbox = inject(IrisGlobalSandbox);

        return new Proxy({}, {
          get: (target, prop) => {
            if (prop === 'stylingMode') {
              const theme = globalSandbox.theme();
              return theme?.strabagControlsEnabled ? 'automatized' : 'underlined';
            }

            return (target as StrakitStylingControlConfigI)[prop];
          },
        }) as StylingMode;
      },
    },

    { provide: LOCALE_ID,
      useFactory: (localeService: LocaleService): string => localeService.getLocale(),
      deps: [LocaleService],
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        role: 'dialog',
        autoFocus: false,
        hasBackdrop: true,
      },
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: { disableTooltipInteractivity: true },
    },
    {
      provide: MAT_LIST_CONFIG,
      useValue: { hideSingleSelectionIndicator: true },
    },
    {
      provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
      useValue: { hideSingleSelectionIndicator: true },
    },
    {
      provide: TitleStrategy,
      useClass: PageTitleStrategyService,
    },
    {
      provide: IrisHotkeysService,
      useClass: IrisHotkeysService,
      deps: [HotkeysService, MatDialog],
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    ToastService,
    {
      provide: SK_TOAST_DURATION,
      useValue: 10000,
    },
    provideHttpClient(
      withInterceptors([translationsProxyRewriteInterceptor]),
      withInterceptorsFromDi()),
    ...provideSkDatetime({
      formatsFactory: datetimeFormatsFactory,
      timezoneFactory: () => inject(IrisUserService).me.timeZone,
    }),
    provideTranslator(STRAKIT_TRANSLATIONS_DICT),
    provideSkSelectConfig(),
    provideHighcharts({
      options: {
        chart: {
          animation: false,
        },
        accessibility: {
          enabled: false,
        },
        plotOptions: {
          series: {
            animation: false,
          },
        },
        credits: { enabled: false },
      },
      modules: () => [
        import('highcharts/esm/modules/no-data-to-display'),
      ],
    }),
    IrisApplicationsService,
    // TODO: Remove when popover will be default behavior for overlays
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
  ],
  bootstrap: [AppRootComponent],
})
export class AppModule {
  constructor(
    @Inject(DOCUMENT) document: Document,
    translate: TranslateService,
  ) {
    translate.onLangChange.pipe(
      tap((event: LangChangeEvent) => {
        document.documentElement.lang = event.lang;
        this._configDate(event.lang);
      }),
    ).subscribe();
  }

  private _configDate(currentLocale: string): void {
    Settings.defaultLocale = LUXON_LOCALE_OVERRIDES[currentLocale] ?? currentLocale;
  }
}

const LUXON_LOCALE_OVERRIDES: Record<string, string> = {
  sr: 'hr',
};
